import React, { Dispatch, useEffect, useState, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import axios from 'axios'
import { useNavigate } from 'react-router-dom'

import { RootState } from '../../store'
import SuccessBody from './SuccessBody'
import LoadingAnimation from '../shared/LoadingAnimation'
import LoadingFailed from '../shared/LoadingFailed'
import StageNavigation from '../shared/StageNavigation'
import * as stages from '../../routes'
import { IParticipant } from '../../store/participant/interfaces'
import { IRegistration } from '../../store/registrations/interfaces'
import { setSuccessful } from '../../store/misc/misc'
import { setParticipant } from '../../store/participant/participant'
import { setPaymentMethod } from '../../store/paymentData'
import { removeAllRegistrations } from '../../store/registrations/registrations'
import { resetAll } from '../../store/actions'
import { deleteLastVisitedRoute, deleteState } from '../../store/localStorage'

export enum RegistrationStatus {
  YES = '1_YES',
  MAYBE = '2_MAYBE',
  NO = '3_NO',
  RECEIVED = '4_RECEIVED',
}

export type PassData = {
  name: string;
  url: string;
}

export type VerifyResponse = {
  message: string
  message_plain: string
  submitted: boolean
  info: {
    rid: number
    registration_states: {
      [key in RegistrationStatus]: string[]
    }
  }
  pass_url: string
  pass_urls?: PassData[]
}

// The following use of any may seem weird, but redux-toolkit
// actually types the dispatch function as Dispatch<any> when
// you use the useDispatch hook.
export const resetRegistration = (dispatch: Dispatch<any>) => {
  dispatch(setParticipant({} as IParticipant))
  dispatch(setPaymentMethod(''))
  dispatch(removeAllRegistrations())
}

/**
 * Transform given parts of the state into a request body
 * the backend actually can understand.
 *
 * @param participant participant data to use
 * @param registrations registrations to use
 * @param paymentData payment data to use
 */
const buildRequestBody = (
  participant: IParticipant,
  registrations: IRegistration[],
  paymentData: RootState['paymentData'],
) => ({
  participant: {
    ...participant,
    gender: participant.gender || "n",
    siblings: participant.siblings?.map(sibling => ({
      ...sibling,
    })),
    children: participant.children?.map(child => ({
      ...child,
    })),
    custom_questions: participant.custom_answers?.map(cq => ({
      id: cq.question_id,
      answer: cq.answer_text,
    })),
  },
  registrations: registrations.map(reg => ({
    event_id: reg.event.event_id,
    priority: reg.priority,
    number_of_adults: reg.numberOfAdults,
    number_of_children: reg.numberOfChildren,
    group_code: reg.groupCode,
    special_notes: reg.specialNotes,
    price_category:
      reg.event.price_categories.length > 0
        ? reg.event.price_categories.find(cat => cat.name === reg.priceCategory)
          ?.id
        : undefined,
    custom_questions: reg.customAnswers.map(cq => ({
      id: cq.id,
      answer: cq.answer,
    })),
    discount_codes: paymentData.discountCodes.map((c) => ({...c, code: c.used_code})),
  })),
  payment: {
    method: paymentData.method,
    ...paymentData.debitData,
    payer_city: `${paymentData.debitData.payer_zip} ${paymentData.debitData.payer_city}`,
    holder_city: `${paymentData.debitData.holder_zip} ${paymentData.debitData.holder_city}`
  },
})

/**
 * Component containing the entire success stage.
 *
 * This will send a registration request to the backend when
 * any of the state related to that request changes.
 */
const SuccessStage = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [failed, setFailed] = useState(false)
  const [respData, setRespData] = useState<VerifyResponse | undefined>(
    undefined,
  )
  const { baseUrl, programId, successful } = useSelector(
    (state: RootState) => state.misc,
  )
  const { data: participant } = useSelector(
    (state: RootState) => state.participant,
  )
  const { list: registrations } = useSelector(
    (state: RootState) => state.registrations,
  )
  const paymentData = useSelector((state: RootState) => state.paymentData)

  const registrationSent = useRef(false);

  // Do the actual request as a side effect and the simply set
  // the related state when the produced promise resolves.
  useEffect(() => {
    if (registrationSent.current) return; // Skip double run, which happens in dev because of <StrictMode>
    registrationSent.current = true;

    console.log('Sending registration request to server...')
    const reqData = buildRequestBody(participant, registrations, paymentData)
    console.debug('< sending request')
    console.debug(reqData)
    const cookie = document.cookie.match(
      '(^|;)\\s*csrftoken\\s*=\\s*([^;]+)',
    )
    const csrftoken = cookie ? cookie[2] : ''
    axios
      .post(
        `${baseUrl}/api/programs/${programId}/participant-registration`,
        reqData,
        { headers: { 'X-CSRFToken': csrftoken } },
      )
      .then(resp => {
        console.debug('> got response from server:')
        console.debug(resp.data)
        setRespData(resp.data)
        dispatch(setSuccessful(resp.data?.submitted))
        if (resp.data?.submitted) {
          resetRegistration(dispatch)
          deleteLastVisitedRoute()
        }
      })
      .catch(() => setFailed(true))
  }, [dispatch])

  return (
    <>
      {!failed && respData !== undefined ? (
        <>
          <SuccessBody resp={respData} />
        </>
      ) : failed ? (
        <LoadingFailed text='Die Anmeldung konnte nicht abgeschickt werden.' />
      ) : (
        <LoadingAnimation text='Anmeldung wird abgeschickt...' />
      )}
      <StageNavigation
        backward={
          !failed && successful
            ? undefined
            : {
              text: stages.VERIFY.longName,
              handler: () => navigate(stages.VERIFY.path, { replace: true }),
            }
        }
        forward={
          !failed && successful
            ? {
              text: 'Neue Anmeldung starten',
              icon: 'sync',
              handler: async () =>  {
                dispatch(resetAll())
                deleteState()
                window.location.reload()
              },
            }
            : undefined
        }
      />
    </>
  )
}

export default SuccessStage
